import { Outlet, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { DocsetProvider } from "../context/DocsetContext";
import type { DocsetManifest } from "../types/docset";
import { LoadingScreen } from "../ui/LoadingScreen";
import { ErrorPage } from "./ErrorPage";
import { ensureTrailingSlash } from "../utils/resources";
import { resolveDocsetDataLocation } from "../utils/dataRoot";

type DocsetLoaderState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; manifest: DocsetManifest }
  | { status: "error"; error: Error };

export function DocsetLoader() {
  const { docsetId } = useParams();
  const [state, setState] = useState<DocsetLoaderState>({ status: "idle" });

  useEffect(() => {
    if (!docsetId) {
      setState({ status: "error", error: new Error("Missing docset identifier") });
      return;
    }
    const resolvedDocsetId = docsetId;
    let cancelled = false;
    const controller = new AbortController();

    async function loadManifest() {
      setState({ status: "loading" });
      try {
        const { manifestUrl, routeBase } = resolveDocsetDataLocation(resolvedDocsetId);
        const response = await fetch(manifestUrl, {
          signal: controller.signal,
          cache: "no-store"
        });
        if (!response.ok) {
          throw new Error(`Failed to load docset manifest (${response.status})`);
        }
        const rawManifest = (await response.json()) as DocsetManifest;
        const providedBase = rawManifest.basePath ?? "";
        const normalizedBase =
          providedBase.startsWith("http://") ||
          providedBase.startsWith("https://") ||
          providedBase.startsWith("/")
            ? ensureTrailingSlash(providedBase)
            : routeBase;
        // The URL segment is the docset's identity for in-app links and data. The renderer
        // derives the manifest `id` from its checkout directory, which need not match the
        // path the docset is published under (the API preview slug, the GitLab project path).
        const manifest: DocsetManifest = {
          ...rawManifest,
          id: resolvedDocsetId,
          basePath: normalizedBase
        };
        if (!cancelled) {
          setState({ status: "ready", manifest });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: "error",
            error: error instanceof Error ? error : new Error(String(error))
          });
        }
      }
    }

    loadManifest();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [docsetId]);

  if (state.status === "idle" || state.status === "loading") {
    return <LoadingScreen hint="Preparing documentation…" />;
  }

  if (state.status === "error") {
    return <ErrorPage error={state.error} />;
  }

  return (
    <DocsetProvider manifest={state.manifest}>
      <Outlet />
    </DocsetProvider>
  );
}
